import { FC, useState } from 'react';
import {
  Box,
  Button,
  Grid,
  InputAdornment,
  TextField,
  Typography,
} from '@mui/material';
import LOGIN_BG from './../../../assets/images/login/login-bg.png';
import APP_LOGO from './../../../assets/images/app-logo.png';
import { Lock, Person } from '@mui/icons-material';
import { Link, useNavigate } from 'react-router-dom';
import { pages } from '../../../utils/common';
import { getAlertBox, getMasterProgressBar } from '../../../utils/helpers_ui';
import { LoginViewProps } from './login.interfaces';
import { CONSTANTS } from '../../../utils/CONSTS';
import { Helmet } from 'react-helmet';
import { useQueryClient } from '@tanstack/react-query';
import { useAuthUserHook } from '../../../hooks/useAuthUserHook';
import { getRbacKey } from '../../../hooks/useRbacHook';
import { localStorageSet } from '../../../utils/localStorage';
import { useTranslation } from 'src/hooks/useTranslationHook';
import isAppWithMock from 'src/utils/isAppWithMock';
import { usePublicBrandingHook } from '../../../hooks/usePublicBrandingHook';

const LoginView: FC<LoginViewProps> = () => {
  const navigate = useNavigate();
  const { translateText } = useTranslation();
  const [uname, setUname] = useState('');
  const [pwd, setPwd] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [isMasterUploadInProgress, setMasterUploadInProgress] = useState(false);
  const queryClient = useQueryClient();
  const loginMutation = useAuthUserHook();
  const publicBranding = usePublicBrandingHook();

  const logoSrc = publicBranding?.logoUrl ?? APP_LOGO;
  const withMockData = isAppWithMock();

  const submitLoginRequest = (e: any) => {
    e.preventDefault();
    // DEV NOTE: uncomment above line and comment below set of lines for skipping login
    if (withMockData) {
      // Check if guardian login
      if (uname === 'guardian' && pwd === 'guardian') {
        localStorageSet('userRole', 'guardian');
        localStorageSet('token', 'mock-guardian-token');
        void queryClient
          .resetQueries({ queryKey: [getRbacKey] })
          .then((data) => {
            console.log('data', data);
            navigate(pages.guardian.path);
          });
        return;
      }
      // Default mock login - go to dashboard
      localStorageSet('userRole', 'staff');
      localStorageSet('token', 'mock-token');
      void queryClient
        .resetQueries({ queryKey: [getRbacKey] })
        .then(() => {
          navigate(pages.dashboard.path);
          console.log(typeof authoriseUser);
        });
      return;
    }
    if (uname?.length > 3 && pwd?.length > 7) {
      authoriseUser();
    } else {
      setErrorMessage(translateText('auth.invalidCredentials'));
    }
  };

  const authoriseUser = async () => {
    try {
      setMasterUploadInProgress(true);
      const response = await loginMutation.mutateAsync({
        username: uname,
        password: pwd,
      });

      if (!response?.token) {
        setErrorMessage(translateText('auth.loginFailedMessage'));
      } else {
        localStorageSet('token', response?.token ?? '');
        try {
          const payload = JSON.parse(atob(response.token.split('.')[1]));
          const instituteId = payload?.institute_ref ?? payload?.instituteId ?? '';
          if (instituteId) {
            localStorageSet('instituteId', instituteId);
          }
        } catch {
          // token decode failed — instituteId will be unavailable
        }
        // Check if user role is guardian
        const userRole = response?.roles?.includes('guardian') ? 'guardian' : 'staff';
        localStorageSet('userRole', userRole);
        await queryClient.resetQueries({ queryKey: [getRbacKey] });

        // Navigate based on role
        if (userRole === 'guardian') {
          navigate(pages.guardian.path);
        } else {
          navigate(pages.dashboard.path);
        }
      }
    } catch (e: any) {
      setErrorMessage(e.message ?? '');
    } finally {
      setMasterUploadInProgress(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>{CONSTANTS.title.login}</title>
      </Helmet>
      <Box
        sx={{
          position: 'relative',
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: { xs: 'center', md: 'flex-start' },
          // Background image on desktop only, positioned on right side
          backgroundImage: {
            xs: 'none',
            md: `url(${LOGIN_BG})`,
          },
          backgroundRepeat: 'no-repeat',
          backgroundSize: 'auto 120%',
          backgroundPosition: { md: 'right center' },
        }}
        data-testid="login-container"
      >
        {isMasterUploadInProgress && getMasterProgressBar()}
        <Box
          sx={{
            width: { xs: '100%', md: '50%' },
            backgroundColor: 'white',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: { xs: '100vh', md: '100vh' },
            boxShadow: {
              xs: 'none',
              md: '0px 0px 20px 0px rgba(0, 0, 0, 0.15)',
            },
            backgroundImage: {
              md: 'none',
              xs: `url(${LOGIN_BG})`,
            },
            backgroundRepeat: 'no-repeat',
            backgroundSize: 'cover',
            backgroundPosition: { md: 'right center' },
          }}
          data-testid="login-form-container"
        >
          <form
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: `100%`,
              flexDirection: 'column',
              width: '100%',
            }}
            onSubmit={submitLoginRequest}
          >
            <Grid
              container
              spacing={2}
              sx={{
                px: 6,
                py: 4,
                width: { xs: '90%', sm: '70%', md: '60%' },
                maxWidth: 400,
                alignItems: 'center',
                justifyContent: 'center',
                minWidth: { xs: 'auto', sm: 250 },
                borderRadius: 2,
                boxShadow: { xs: '0px 0px 10px 0px rgba(0, 0, 0, 0.5)', md: 'none' },
                backgroundColor: { xs: 'white' },
                animation: 'fadeInUp 0.5s ease-out',
                '@keyframes fadeInUp': {
                  '0%': {
                    opacity: 0,
                    transform: 'translateY(20px)',
                  },
                  '100%': {
                    opacity: 1,
                    transform: 'translateY(0)',
                  },
                },
              }}
              data-testid="login-form"
            >
              <Grid
                item
                xs={12}
                sx={{ display: 'flex', justifyContent: 'center' }}
              >
                <img
                  src={logoSrc}
                  alt={publicBranding?.instituteName ?? 'app-logo'}
                  style={{
                    height: 'auto',
                    maxHeight: 100,
                    width: 'auto',
                    maxWidth: '100%',
                  }}
                />
              </Grid>
              <Grid
                item
                xs={12}
                sx={{
                  marginBottom: `8px !important`,
                }}
              >
                <Typography
                  variant="h4"
                  sx={{
                    textAlign: 'center',
                    fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' },
                  }}
                >
                  {translateText('auth.signIn')}
                </Typography>
              </Grid>
              {errorMessage ? (
                <Grid item xs={12}>
                  {getAlertBox(
                    translateText('common.error'),
                    errorMessage ?? translateText('auth.loginFailed'),
                    'error'
                  )}
                </Grid>
              ) : null}
              <Grid
                item
                xs={12}
                sx={{
                  marginBottom: `8px !important`,
                }}
              >
                <TextField
                  id="username"
                  label={translateText('auth.username')}
                  size="medium"
                  variant="standard"
                  sx={{
                    width: `100%`,
                    transition: 'all 0.2s ease',
                    '& .MuiInputBase-input': {
                      fontSize: '16px', // Prevent iOS zoom
                      minHeight: '44px', // Touch-friendly
                    },
                    '& .MuiInputBase-root': {
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        transform: 'translateY(-1px)',
                      },
                    },
                  }}
                  onChange={(e) => setUname(e.target.value ?? '')}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <Person sx={{ transition: 'transform 0.2s ease', '&:hover': { transform: 'scale(1.1)' } }} />
                      </InputAdornment>
                    ),
                  }}
                  helperText={translateText('auth.usernameHelper')}
                  data-testid="username-input"
                />
              </Grid>
              <Grid
                item
                xs={12}
                sx={{
                  marginBottom: `8px !important`,
                }}
              >
                <TextField
                  id="password"
                  label={translateText('auth.password')}
                  type="password"
                  size="medium"
                  variant="standard"
                  autoComplete="current-password"
                  sx={{
                    width: `100%`,
                    transition: 'all 0.2s ease',
                    '& .MuiInputBase-input': {
                      fontSize: '16px', // Prevent iOS zoom
                      minHeight: '44px', // Touch-friendly
                    },
                    '& .MuiInputBase-root': {
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        transform: 'translateY(-1px)',
                      },
                    },
                  }}
                  onChange={(e) => setPwd(e.target.value ?? '')}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <Lock sx={{ transition: 'transform 0.2s ease', '&:hover': { transform: 'scale(1.1)' } }} />
                      </InputAdornment>
                    ),
                  }}
                  helperText={translateText('auth.passwordHelper')}
                  data-testid="password-input"
                />
              </Grid>
              <Grid
                item
                xs={12}
                sx={{
                  marginBottom: `8px !important`,
                }}
              >
                <Button
                  id="submitForm"
                  variant="contained"
                  sx={{
                    marginTop: 2,
                    width: `100%`,
                    minHeight: { xs: '44px', sm: '36px' },
                    fontSize: { xs: '16px', sm: '14px' },
                    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: '0 8px 20px rgba(58, 54, 184, 0.4)',
                    },
                    '&:active': {
                      transform: 'translateY(0)',
                    },
                  }}
                  type="submit"
                  data-testid="login-submit-button"
                >
                  {translateText('common.login')}
                </Button>
                <Link to={'/'}>
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'primary.main',
                      textDecoration: 'underline',
                      textAlign: 'center',
                      mt: 1,
                      fontSize: { xs: '0.875rem', sm: '0.875rem' },
                    }}
                    data-testid="forgot-password-link"
                  >
                    {translateText('auth.forgotPassword')}
                  </Typography>
                </Link>
              </Grid>
            </Grid>
          </form>
        </Box>
      </Box>
    </>
  );
};

export default LoginView;
